import { useCallback, useEffect, useMemo, useState } from 'react'
import { Download, Search } from 'lucide-react'
import { fetchAgents } from '@/api/agents'
import { fetchCustomers } from '@/api/customers'
import { useAuth } from '@/auth/AuthContext'
import { ColumnPicker } from '@/components/ColumnPicker'
import { CustomerFilter } from '@/components/CustomerFilter'
import { AgentsTable } from '@/components/tables/AgentsTable'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { DEVICE_COLUMNS } from '@/config/columns'
import {
  exportDevicesCsv,
  fetchAllFilteredAgents,
} from '@/lib/exportDevicesCsv'
import { columnsInVisibleOrder, useColumnVisibility } from '@/hooks/useColumnVisibility'

const PAGE_SIZE = 50
const STORAGE_KEY = 'devices-columns-v4'

export function Agents() {
  const { isCustomer, canFilterCustomers } = useAuth()
  const deviceColumns = useMemo(
    () => DEVICE_COLUMNS.filter((c) => !(isCustomer && c.hideForCustomer)),
    [isCustomer],
  )
  const {
    visibleIds,
    toggleColumn,
    reorderVisible,
    showAll,
    resetDefaults,
  } = useColumnVisibility(STORAGE_KEY, deviceColumns)

  const [agents, setAgents] = useState([])
  const [customers, setCustomers] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pages, setPages] = useState(1)
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState('')

  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [customerIds, setCustomerIds] = useState([])
  const [onlineFilter, setOnlineFilter] = useState('')
  const [sortBy, setSortBy] = useState('hostname')
  const [sortDir, setSortDir] = useState('asc')

  const visibleColumns = useMemo(
    () => columnsInVisibleOrder(deviceColumns, visibleIds),
    [deviceColumns, visibleIds],
  )

  const loadAgents = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const params = {
        page,
        page_size: PAGE_SIZE,
        search: search || undefined,
        sort_by: sortBy,
        sort_dir: sortDir,
      }
      if (onlineFilter === 'online') params.online = true
      if (onlineFilter === 'offline') params.online = false
      const data = await fetchAgents(params, customerIds)
      setAgents(data.items)
      setTotal(data.total)
      setPages(data.pages)
    } catch {
      setError('Could not load devices.')
    } finally {
      setLoading(false)
    }
  }, [page, search, customerIds, onlineFilter, sortBy, sortDir])

  function handleSort(column) {
    if (!visibleIds.includes(column)) return
    if (sortBy === column) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(column)
      setSortDir('asc')
    }
    setPage(1)
  }

  useEffect(() => {
    if (canFilterCustomers) {
      fetchCustomers().then(setCustomers).catch(() => {})
    }
  }, [canFilterCustomers])

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim())
      setPage(1)
    }, 350)
    return () => clearTimeout(t)
  }, [searchInput])

  useEffect(() => {
    loadAgents()
  }, [loadAgents])

  function handleFilterChange(updater) {
    updater()
    setPage(1)
  }

  async function handleExport() {
    setExporting(true)
    setError('')
    try {
      const agents = await fetchAllFilteredAgents({
        search,
        customerIds,
        onlineFilter,
        sortBy,
        sortDir,
      })
      exportDevicesCsv(agents, visibleColumns)
    } catch {
      setError('Could not export devices.')
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="search">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder="Hostname, display name, processor, domain…"
                  className="pl-9"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>
            </div>
            {canFilterCustomers && (
              <CustomerFilter
                id="devices-customer"
                value={customerIds}
                onChange={(v) => handleFilterChange(() => setCustomerIds(v))}
                customers={customers}
              />
            )}
            <div className="space-y-2">
              <Label htmlFor="online">Online status</Label>
              <select
                id="online"
                className="flex h-10 w-full rounded-md border border-input bg-secondary/50 px-3 text-sm text-foreground"
                value={onlineFilter}
                onChange={(e) =>
                  handleFilterChange(() => setOnlineFilter(e.target.value))
                }
              >
                <option value="">All</option>
                <option value="online">Online</option>
                <option value="offline">Offline</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
          <CardTitle>
            Devices {total > 0 && <span className="text-muted-foreground">({total})</span>}
          </CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleExport}
              disabled={exporting || loading || total === 0}
            >
              <Download className="h-4 w-4" />
              {exporting ? 'Exporting…' : 'Export'}
            </Button>
            <ColumnPicker
              columns={deviceColumns}
              visibleIds={visibleIds}
              onToggle={toggleColumn}
              onReorder={reorderVisible}
              onShowAll={showAll}
              onResetDefaults={resetDefaults}
              buttonLabel="Edit columns"
            />
          </div>
        </CardHeader>
        <CardContent>
          {error && <p className="mb-4 text-sm text-destructive">{error}</p>}
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : (
            <AgentsTable
              agents={agents}
              sortBy={sortBy}
              sortDir={sortDir}
              onSort={handleSort}
              hideCustomer={!canFilterCustomers}
              visibleColumnIds={visibleIds}
            />
          )}
          {pages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Page {page} of {pages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= pages || loading}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
